/**
 * Generic polling hook with visibility-aware pause/resume.
 */

import { useCallback, useEffect, useRef, useState } from "react";

type UsePollingOptions = {
  enabled?: boolean;
};

type UsePollingResult<T> = {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
};

export function usePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs: number,
  opts?: UsePollingOptions,
): UsePollingResult<T> {
  const enabled = opts?.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const doFetch = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetcherRef.current();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Immediate first fetch
    void doFetch();

    let timer: ReturnType<typeof setInterval> | null = null;

    function startPolling() {
      if (timer) return;
      timer = setInterval(() => void doFetch(), intervalMs);
    }

    function stopPolling() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function handleVisibility() {
      if (document.hidden) {
        stopPolling();
      } else {
        void doFetch(); // immediate fetch on return
        startPolling();
      }
    }

    startPolling();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, intervalMs, doFetch]);

  return { data, loading, error, refresh: doFetch };
}
